import { lazy } from "react";
import { Route, Redirect } from "wouter";
import type { RouteHelpers } from "./types";
import { AdminRoleProvider } from "@/contexts/AdminRoleContext";

const Profile = lazy(() => import("@/pages/Profile"));
const MyProfile = lazy(() => import("@/pages/MyProfile"));
const Messages = lazy(() => import("@/pages/Messages"));
const Directory = lazy(() => import("@/pages/Directory"));
const Settings = lazy(() => import("@/pages/Settings"));
const Account = lazy(() => import("@/pages/Account"));
const Premium = lazy(() => import("@/pages/Premium"));
const PremiumSuccess = lazy(() => import("@/pages/Premium").then(m => ({ default: m.PremiumSuccess })));
const PremiumCancel = lazy(() => import("@/pages/Premium").then(m => ({ default: m.PremiumCancel })));
const MembershipLanding = lazy(() => import("@/pages/MembershipLanding"));
const MembershipInsider = lazy(() => import("@/pages/MembershipInsider"));
const MembershipExecutive = lazy(() => import("@/pages/MembershipExecutive"));
const MembershipBoardroom = lazy(() => import("@/pages/MembershipBoardroom"));
const MembershipApply = lazy(() => import("@/pages/MembershipApply"));
const MembershipUpgrade = lazy(() => import("@/pages/MembershipUpgrade"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const TeamPricing = lazy(() => import("@/pages/TeamPricing"));
const Dealroom = lazy(() => import("@/pages/Dealroom"));
const BoardMatch = lazy(() => import("@/pages/BoardMatch"));
const BoardOpportunities = lazy(() => import("@/pages/BoardOpportunities"));
const BoardOpportunityDetail = lazy(() => import("@/pages/BoardOpportunityDetail"));
const SponsorDashboard = lazy(() => import("@/pages/SponsorDashboard"));
const ContributorDashboard = lazy(() => import("@/pages/contributor-dashboard"));
const ContributorOnboarding = lazy(() => import("@/pages/ContributorOnboarding"));
const VolunteerOnboarding = lazy(() => import("@/pages/VolunteerOnboarding"));
const VolunteerSubmission = lazy(() => import("@/pages/VolunteerSubmission"));
const VolunteerDashboard = lazy(() => import("@/pages/VolunteerDashboard"));
const ContentReview = lazy(() => import("@/pages/ContentReview"));
const ContentSubmit = lazy(() => import("@/pages/ContentSubmit"));
const ContentPeerReview = lazy(() => import("@/pages/ContentPeerReview"));
const AgentLogs = lazy(() => import("@/pages/AgentLogs"));
const CapitalLanding = lazy(() => import("@/pages/capital/CapitalLanding"));
const DealReadiness = lazy(() => import("@/pages/capital/DealReadiness"));
const DealRoom = lazy(() => import("@/pages/capital/DealRoom"));
const InvestorApplication = lazy(() => import("@/pages/capital/InvestorApplication"));
const PortfolioHub = lazy(() => import("@/pages/capital/PortfolioHub"));

const RecommendStartup = lazy(() => import("@/pages/capital/RecommendStartup"));
const FounderWorkspace = lazy(() => import("@/pages/founder/Workspace"));
const OnePageBrief = lazy(() => import("@/pages/founder/OnePageBrief"));
const InvestorUpdates = lazy(() => import("@/pages/founder/InvestorUpdates"));
const AskBoard = lazy(() => import("@/pages/community/AskBoard"));
const SettingsRoles = lazy(() => import("@/pages/settings/Roles"));
const EmployerVerification = lazy(() => import("@/pages/settings/EmployerVerification"));
const BoardCandidateSettings = lazy(() => import("@/pages/settings/BoardCandidate"));
const PersonalizationPreferences = lazy(() => import("@/pages/PersonalizationPreferences"));
const IntroPreferences = lazy(() => import("@/pages/IntroPreferences"));
const AdvisorDashboard = lazy(() => import("@/pages/advisor/Dashboard"));
const AdvisorDiligenceQueue = lazy(() => import("@/pages/advisor/DiligenceQueue"));
const AcceptAdvisoryInvitation = lazy(() => import("@/pages/advisor/AcceptInvitation"));
const LpDashboard = lazy(() => import("@/pages/lp/Dashboard"));
const LpReferrals = lazy(() => import("@/pages/lp/Referrals"));
const LpNominate = lazy(() => import("@/pages/lp/Nominate"));
const LpBrandHub = lazy(() => import("@/pages/lp/BrandHub"));
const LpDirectory = lazy(() => import("@/pages/lp/Directory"));
const JuryDashboard = lazy(() => import("@/pages/jury/Dashboard"));

export function MemberRoutes({ LazyRoute, user }: RouteHelpers) {
    return (
        <>
            {/* Profile & messaging */}
            <Route path="/profile/:id">{() => <LazyRoute component={Profile} />}</Route>
            <Route path="/profile">{() => <LazyRoute component={MyProfile} />}</Route>
            <Route path="/bio/:slug">{() => <LazyRoute component={Profile} />}</Route>
            <Route path="/messages">{() => <LazyRoute component={Messages} />}</Route>
            <Route path="/directory">{() => <LazyRoute component={Directory} />}</Route>
            {/* Membership & billing */}
            <Route path="/premium/success">{() => <LazyRoute component={PremiumSuccess} />}</Route>
            <Route path="/premium/cancel">{() => <LazyRoute component={PremiumCancel} />}</Route>
            <Route path="/premium">{() => <Redirect to="/membership" />}</Route>
            <Route path="/membership/insider">{() => <LazyRoute component={MembershipInsider} />}</Route>
            <Route path="/membership/executive">{() => <LazyRoute component={MembershipExecutive} />}</Route>
            <Route path="/membership/boardroom">{() => <LazyRoute component={MembershipBoardroom} />}</Route>
            <Route path="/membership/apply">{() => <LazyRoute component={MembershipApply} />}</Route>
            <Route path="/membership/success">{() => <LazyRoute component={PremiumSuccess} />}</Route>
            <Route path="/membership/cancel">{() => <LazyRoute component={PremiumCancel} />}</Route>
            <Route path="/membership/upgrade">{() => <LazyRoute component={MembershipUpgrade} />}</Route>
            <Route path="/membership">{() => <LazyRoute component={MembershipLanding} />}</Route>
            <Route path="/pricing/teams">{() => <LazyRoute component={TeamPricing} />}</Route>
            <Route path="/pricing">{() => user?.isAdmin ? <LazyRoute component={Pricing} /> : <Redirect to="/membership/upgrade" />}</Route>
            {/* Settings & account */}
            <Route path="/settings/roles">{() => <LazyRoute component={SettingsRoles} />}</Route>
            <Route path="/settings/employer-verification">{() => <LazyRoute component={EmployerVerification} />}</Route>
            <Route path="/settings/board-candidate">{() => <LazyRoute component={BoardCandidateSettings} />}</Route>
            <Route path="/settings/personalization">{() => <LazyRoute component={PersonalizationPreferences} />}</Route>
            <Route path="/settings/profile">{() => <Redirect to="/profile" />}</Route>
            <Route path="/settings">{() => <Redirect to="/account" />}</Route>
            <Route path="/account">{() => <LazyRoute component={Account} />}</Route>
            <Route path="/intro-preferences">{() => <LazyRoute component={IntroPreferences} />}</Route>
            {/* Capital / Deals */}
            <Route path="/capital/deal-readiness">{() => <LazyRoute component={DealReadiness} />}</Route>
            <Route path="/capital/dealroom">{() => <LazyRoute component={DealRoom} />}</Route>
            <Route path="/capital/investor-apply">{() => <LazyRoute component={InvestorApplication} />}</Route>
            <Route path="/capital/portfolio-hub">{() => <LazyRoute component={PortfolioHub} />}</Route>

            <Route path="/capital/recommend-startup">{() => <LazyRoute component={RecommendStartup} />}</Route>
            {/* Fund apply routes (chiefintech.capital/apply/*) */}

            <Route path="/capital/fund/apply/investor">{() => <LazyRoute component={InvestorApplication} />}</Route>
            <Route path="/capital/fund/apply/recommend">{() => <LazyRoute component={RecommendStartup} />}</Route>
            {/* Fund operational routes */}
            <Route path="/capital/fund/dealroom">{() => <LazyRoute component={DealRoom} />}</Route>
            <Route path="/capital/fund/deal-readiness">{() => <LazyRoute component={DealReadiness} />}</Route>
            <Route path="/capital/fund/portfolio-hub">{() => <LazyRoute component={PortfolioHub} />}</Route>
            {/* Legacy redirects */}
            <Route path="/capital/fund/old">{() => <Redirect to="/capital/fund" />}</Route>
            <Route path="/capital/risk-factors">{() => <Redirect to="/capital/fund/risk-factors" />}</Route>
            <Route path="/capital">{() => <LazyRoute component={CapitalLanding} />}</Route>
            <Route path="/dealroom">{() => <LazyRoute component={Dealroom} />}</Route>
            <Route path="/board-match">{() => <LazyRoute component={BoardMatch} />}</Route>
            <Route path="/board-opportunities/:id">{() => <LazyRoute component={BoardOpportunityDetail} />}</Route>
            <Route path="/board-opportunities">{() => <LazyRoute component={BoardOpportunities} />}</Route>
            {/* Founder */}
            <Route path="/founder/workspace">{() => <LazyRoute component={FounderWorkspace} />}</Route>
            <Route path="/founder/one-page-brief">{() => <LazyRoute component={OnePageBrief} />}</Route>
            <Route path="/founder/investor-updates">{() => <LazyRoute component={InvestorUpdates} />}</Route>
            {/* Advisor / LP */}
            <Route path="/advisor/dashboard">{() => <LazyRoute component={AdvisorDashboard} />}</Route>
            <Route path="/advisor/diligence">{() => <LazyRoute component={AdvisorDiligenceQueue} />}</Route>
            <Route path="/advisory-board/accept-invitation">{() => <LazyRoute component={AcceptAdvisoryInvitation} />}</Route>
            <Route path="/lp/referrals">{() => <LazyRoute component={LpReferrals} />}</Route>
            <Route path="/lp/nominate">{() => <LazyRoute component={LpNominate} />}</Route>
            <Route path="/lp/brand-hub">{() => <LazyRoute component={LpBrandHub} />}</Route>
            <Route path="/lp/directory">{() => <LazyRoute component={LpDirectory} />}</Route>
            {/* /lp is a legacy alias — the canonical LP dashboard is /capital/fund/lp */}
            <Route path="/lp">{() => <Redirect to="/capital/fund/lp" />}</Route>
            {/* LP routes under chiefintech.capital/lp/* */}
            <Route path="/capital/fund/lp/referrals">{() => <LazyRoute component={LpReferrals} />}</Route>
            <Route path="/capital/fund/lp/nominate">{() => <LazyRoute component={LpNominate} />}</Route>
            <Route path="/capital/fund/lp/brand-hub">{() => <LazyRoute component={LpBrandHub} />}</Route>
            <Route path="/capital/fund/lp/directory">{() => <LazyRoute component={LpDirectory} />}</Route>
            <Route path="/capital/fund/lp">{() => <LazyRoute component={LpDashboard} />}</Route>
            <Route path="/capital/fund/jury">{() => <LazyRoute component={JuryDashboard} />}</Route>
            <Route path="/jury">{() => <Redirect to="/capital/fund/jury" />}</Route>
            {/* Community */}
            <Route path="/community/ask-board">{() => <LazyRoute component={AskBoard} />}</Route>
            {/* Sponsor / Contributor */}
            <Route path="/sponsor/dashboard">{() => <LazyRoute component={SponsorDashboard} />}</Route>
            <Route path="/contributor/onboarding">{() => <LazyRoute component={ContributorOnboarding} />}</Route>
            <Route path="/contributor">{() => <LazyRoute component={ContributorDashboard} />}</Route>
            <Route path="/volunteer/onboarding">{() => <LazyRoute component={VolunteerOnboarding} />}</Route>
            <Route path="/volunteer/dashboard">{() => <LazyRoute component={VolunteerDashboard} />}</Route>
            <Route path="/volunteer/submit/:taskId">{() => <LazyRoute component={VolunteerSubmission} />}</Route>
            <Route path="/content/submit">{() => <LazyRoute component={ContentSubmit} />}</Route>
            <Route path="/content/peer-review">{() => <LazyRoute component={ContentPeerReview} />}</Route>
        </>
    );
}
